import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {TimelineFilter} from 'yougile-model'
import {LinkButton} from '@components/common/button/button'
import {TextInput} from '@components/common/text-input/text-input'
import {DateFilter} from '@components/statistics/stats-reports/filter-components/date-filter'
import {LocationFilter} from '@components/statistics/stats-reports/filter-components/location-filter'
import {UserFilter} from '@components/statistics/stats-reports/filter-components/user-filter'
import {globals} from '@extensions/globals-base'
import {useStatsTimelineStore} from '../stats-timeline-store'
import {ActionFilter} from './filter-action'
import {FilterDeletion} from './filter-deletion'

const TwLabel = tw.div`
  space-y-8
  my-4
  mr-8
  min-w-160
  text-right
  text-12
`

const TwWrapper = tw.div`
  flex
  items-baseline
`

const TwFilter = tw.div`
  flex-1
  my-4
`

interface Props {
  initialFilter: TimelineFilter
}

interface FilterProps {
  currentFilter: TimelineFilter
  updateFilter: <T extends keyof TimelineFilter>(
    filterName: T
  ) => (filter: TimelineFilter[T]) => void
  onOpenChange?: (isOpen: boolean) => void
}

const Location = ({currentFilter, updateFilter, onOpenChange}: FilterProps) => (
  <TwWrapper>
    <TwLabel>{Lang.msg.statsTimeline.location}</TwLabel>
    <TwFilter>
      <LocationFilter
        showInputForEmptyFilters
        filters={currentFilter.located}
        update={updateFilter('located')}
        dontOpen
        hideSubtasksOptions
        hideRemoveButton
        onOpenChange={isOpen => onOpenChange(isOpen)}
      />
    </TwFilter>
  </TwWrapper>
)

const User = ({currentFilter, updateFilter, onOpenChange}: FilterProps) => (
  <TwWrapper>
    <TwLabel>{Lang.msg.statsTimeline.initiator}</TwLabel>
    <TwFilter>
      <UserFilter
        filters={currentFilter.createdBy}
        update={updateFilter('createdBy')}
        dontOpen
        withInputDecryption
        showRemoveButton={false}
        className={'w-160'}
        onOpenChange={isOpen => {
          onOpenChange(isOpen)
        }}
        showUnderHeadElement={false}
        placeholder={Lang.msg.statsTimeline.allUsers}
      />
    </TwFilter>
  </TwWrapper>
)

const Date = ({currentFilter, updateFilter, onOpenChange}: FilterProps) => (
  <TwWrapper>
    <TwLabel>{Lang.msg.statsTimeline.eventDate}</TwLabel>
    <TwFilter>
      <DateFilter
        filter={currentFilter.createdAt}
        update={updateFilter('createdAt')}
        isAny
        placeholder={Lang.msg.statsTimeline.any}
        dontOpen
        showRemoveButton={false}
        onOpenChange={isOpen => {
          onOpenChange(isOpen)
        }}
      />
    </TwFilter>
  </TwWrapper>
)

const Action = ({currentFilter, updateFilter, onOpenChange}: FilterProps) => (
  <TwWrapper>
    <TwLabel>{Lang.msg.statsTimeline.action}</TwLabel>
    <TwFilter>
      <ActionFilter
        filter={currentFilter.action}
        update={updateFilter('action')}
        showRemoveButton={false}
        onOpenChange={isOpen => {
          onOpenChange(isOpen)
        }}
        placeholder={Lang.msg.statsTimeline.allActions}
        withInputDecryption
      />
    </TwFilter>
  </TwWrapper>
)

const Title = ({currentFilter, updateFilter}: FilterProps) => (
  <TwWrapper>
    <TwLabel>{Lang.msg.statsTimeline.filterName}</TwLabel>
    <TwFilter>
      <TextInput
        className="w-200 text-12"
        value={currentFilter.title}
        onChange={updateFilter('title')}
        placeholder={Lang.msg.statsTimeline.enterNameOfFilter}
      />
    </TwFilter>
  </TwWrapper>
)

// eslint-disable-next-line max-lines-per-function
export const StatsTimelineFilter = observer((props: Props) => {
  const [, store] = useStatsTimelineStore()
  const {initialFilter} = props
  useEffect(() => changeFilter(initialFilter), [initialFilter])
  const [currentFilter, changeFilter] = useState(initialFilter)

  const saveNew = () => {
    const filter = {
      ...currentFilter,
      id: Util.longId(),
      relevantCompanyId: globals.getCompanyId()
    }
    store.saveNewFilter(filter)
  }

  const updateCurrent = () => {
    store.updateCurrentFilter(currentFilter)
  }

  const deleteCurrent = () => {
    store.deleteCurrentFilter(currentFilter.id)
    store.toggleFilter()
  }

  const saveFromCurrentAsNew = () => {
    store.saveFromCurrentAsNewFilter({
      ...currentFilter,
      id: Util.longId(),
      relevantCompanyId: globals.getCompanyId()
    })
  }

  const getValue = (filter: TimelineFilter[keyof TimelineFilter]) => {
    return typeof filter === 'string' ? filter.trim() : filter
  }

  const updateFilter =
    <T extends keyof TimelineFilter>(filterName: T) =>
    (filter: TimelineFilter[T]) => {
      changeFilter({
        ...currentFilter,
        [filterName]: getValue(filter)
      })
    }

  const onOpenChange = (isOpen: boolean) => {
    if (currentFilter !== initialFilter && !isOpen) {
      store.setActiveFilterForce({
        ...currentFilter
      })
    }
  }
  const isNew =
    !initialFilter ||
    !store.profileHub.data.eventFeedFilters?.filter(
      f => f.id === initialFilter.id
    )?.length

  return (
    <div className="mt-20 ml-42">
      <div className="flex flex-col pl-12">
        <Location
          updateFilter={updateFilter}
          currentFilter={currentFilter}
          onOpenChange={isOpen => {
            onOpenChange(isOpen)
          }}
        />
        <User
          updateFilter={updateFilter}
          currentFilter={currentFilter}
          onOpenChange={isOpen => {
            onOpenChange(isOpen)
          }}
        />
        <Date
          updateFilter={updateFilter}
          currentFilter={currentFilter}
          onOpenChange={isOpen => {
            onOpenChange(isOpen)
          }}
        />
        <Action
          updateFilter={updateFilter}
          currentFilter={currentFilter}
          onOpenChange={isOpen => {
            onOpenChange(isOpen)
          }}
        />
        <div className="flex items-baseline mt-16">
          <Title updateFilter={updateFilter} currentFilter={currentFilter} />
          <div>
            {!isNew ? (
              <>
                <LinkButton
                  $small
                  onClick={updateCurrent}
                  $disabled={!currentFilter.title.length}
                >
                  {Lang.msg.statsTimeline.saveChanges}
                </LinkButton>
                <FilterDeletion deleteCurrent={deleteCurrent} />
                {currentFilter.title !== initialFilter.title && (
                  <LinkButton
                    $small
                    onClick={saveFromCurrentAsNew}
                    $disabled={!currentFilter.title.length}
                  >
                    {Lang.msg.statsTimeline.saveAsNewFilter}
                  </LinkButton>
                )}
              </>
            ) : (
              <LinkButton
                $small
                $disabled={!currentFilter.title.length}
                onClick={saveNew}
              >
                {Lang.msg.statsTimeline.saveFilter}
              </LinkButton>
            )}
          </div>
        </div>
      </div>
    </div>
  )
})
